import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isArtifactId } from '@/artifacts/navigation-map';
import { DOCUMENT_TITLE_SEPARATOR } from '@/constants/ui';
import {
  MAP_CONFIG_SECTION,
  ROUTES,
  SECTION_SEGMENT_INDEX,
} from '@/routes/routes';

const BRAND_TITLE_KEY = 'app.title';
const MAP_SECTION_TITLE_KEY = 'appShell.sidebar.map';
const ARTIFACT_SEGMENT_INDEX = SECTION_SEGMENT_INDEX + 1;
// Invitation path prefix derived from the parameterized route ("/invite/:token" → "/invite/").
const INVITATION_PATH_PREFIX = ROUTES.INVITATION.slice(
  0,
  ROUTES.INVITATION.indexOf(':')
);

// Full i18n keys as literals (never assemble keys at runtime — breaks i18n
// extraction tooling). Reuses the same sidebar labels as the nav items.
const SECTION_TITLE_KEYS: Record<string, string> = {
  dashboard: 'appShell.sidebar.dashboard',
  map: MAP_SECTION_TITLE_KEY,
  tasks: 'appShell.sidebar.tasks',
  notes: 'appShell.sidebar.notes',
  obstructions: 'appShell.sidebar.obstructions',
  settings: 'appShell.sidebar.settings',
  // First-run/edit gate route (`/w/:shortId/map-config`) — reuses the
  // configurator's own heading so the tab reads "GeoMarkup - Konfiguracja mapy".
  [MAP_CONFIG_SECTION]: 'mapSetup.title',
};

/**
 * Resolve the i18n key for the current route's page name, or null for
 * transient routes (auth callback, workspace redirects) that show the bare
 * brand name.
 */
function resolvePageTitleKey(pathname: string): string | null {
  if (pathname === ROUTES.ONBOARDING) return 'pageTitles.onboarding';
  if (pathname.startsWith(INVITATION_PATH_PREFIX))
    return 'pageTitles.invitation';

  const segments = pathname.split('/');
  const section = segments[SECTION_SEGMENT_INDEX];
  if (!section) return null;

  // Map background: prefer the specific artifact label when one is active.
  if (section === 'map') {
    const artifact = segments[ARTIFACT_SEGMENT_INDEX];
    if (artifact && isArtifactId(artifact)) {
      return SECTION_TITLE_KEYS[artifact] ?? MAP_SECTION_TITLE_KEY;
    }
    return MAP_SECTION_TITLE_KEY;
  }

  return SECTION_TITLE_KEYS[section] ?? null;
}

/**
 * Keeps the browser tab title in sync with the active route as
 * "GeoMarkup - <Page>" (e.g. "GeoMarkup - Dashboard"), localized to the
 * current language. Mounted once in the router's RootLayout so it owns
 * document.title for every page.
 */
export function usePageTitle(): void {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const brand = t(BRAND_TITLE_KEY);
    const pageTitleKey = resolvePageTitleKey(pathname);
    document.title = pageTitleKey
      ? `${brand}${DOCUMENT_TITLE_SEPARATOR}${t(pageTitleKey)}`
      : brand;
  }, [pathname, t, i18n.language]);
}
